// JOBE-0044.6: EntityTypesProvider — route-level provider that owns the
// single GET /api/entity-types fetch and broadcasts the result via
// EntityTypesContext. Mounted by LibraryListPage as a peer of
// LibraryRail so the rail's TypeRailBlock, the inspector (.13), and the
// page-level load/error variant (.14) all read a single source of
// truth.
//
// Tri-state transitions:
//   - mount → { status: 'loading', types: [], error: null }
//   - fetch ok → { status: 'ready', types: <rows>, error: null }
//   - transport / non-2xx → { status: 'error', types: [], error: '<msg>' }
//
// No re-fetch within the same route mount in v1 — entity-type
// registries change infrequently. A route remount (back/forward to
// /library) re-issues the fetch.

import { useEffect, useMemo, useRef, useState } from 'react';
import { EntityTypesContext } from './EntityTypesContext.js';

export function EntityTypesProvider({ children }) {
  const [state, setState] = useState(() => ({
    status: 'loading',
    types: [],
    error: null,
  }));
  // Guard against double-fetch under React.StrictMode's intentional
  // double-effect-invoke in dev. The provider's contract is "one
  // fetch per route mount" — StrictMode double-invocation should not
  // surface as two network calls.
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return undefined;
    fetchedRef.current = true;
    const ctrl = new AbortController();
    let alive = true;
    fetch('/api/entity-types', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({
            status: 'error',
            types: [],
            error: `http_${r.status}`,
          });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const types = Array.isArray(j?.types) ? j.types : [];
        setState({ status: 'ready', types, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({
          status: 'error',
          types: [],
          error: err?.message || String(err),
        });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // Reference-stable value across unrelated re-renders.
  const value = useMemo(() => state, [state]);

  return (
    <EntityTypesContext.Provider value={value}>
      {children}
    </EntityTypesContext.Provider>
  );
}
